import { useMemo, useRef, useState } from "react";
import { ContextMenu, toast } from "@/ui";
import { cn } from "@/lib/cn";
import type { Contact } from "@/lib/api";

async function copyText(text: string, label: string) {
    try {
        await navigator.clipboard.writeText(text);
        toast.success(`Copied ${label}`);
    } catch {
        toast.error("Couldn't copy to clipboard");
    }
}

/** A single addressee. `name` is the display name (may be null for raw input). */
export interface Recipient {
    name: string | null;
    address: string;
}

export type RecipientField = "to" | "cc" | "bcc";

export interface RecipientBuckets {
    to:  Recipient[];
    cc:  Recipient[];
    bcc: Recipient[];
}

export const emptyRecipientBuckets = (): RecipientBuckets => ({ to: [], cc: [], bcc: [] });

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Parse free-form input (`"Name" <addr>, addr2; addr3`) into recipients.
 *  Splits on any run of commas, semicolons, or newlines (\n, \r, \r\n), so a
 *  pasted block of mixed-delimiter addresses fans out into separate recipients. */
export function parseRecipients(input: string): Recipient[] {
    const out: Recipient[] = [];
    for (const partRaw of input.split(/[,;\r\n]+/)) {
        const part = partRaw.trim();
        if (!part) continue;
        const named = part.match(/^"?([^"<]*?)"?\s*<([^>]+)>$/);
        if (named) {
            const addr = named[2].trim().toLowerCase();
            if (EMAIL_RX.test(addr)) out.push({ name: named[1].trim() || null, address: addr });
            continue;
        }
        const addr = part.toLowerCase();
        if (EMAIL_RX.test(addr)) out.push({ name: null, address: addr });
    }
    return out;
}

/** `"Name" <addr>` (or bare address) for display / API submission. */
export function formatRecipient(r: Recipient): string {
    return r.name ? `"${r.name.replace(/"/g, "")}" <${r.address}>` : r.address;
}

/** Comma-joined header string for one bucket, or undefined if empty. */
export function recipientsToHeader(list: Recipient[]): string | undefined {
    return list.length ? list.map(formatRecipient).join(", ") : undefined;
}

/** Move/insert a recipient into a target bucket, removing it from every other
 *  bucket first so an address only ever lives in one place. */
function placeRecipient(
    buckets: RecipientBuckets,
    target: RecipientField,
    recipient: Recipient,
): RecipientBuckets {
    const addr = recipient.address.toLowerCase();
    const strip = (list: Recipient[]) => list.filter(r => r.address.toLowerCase() !== addr);
    const next: RecipientBuckets = { to: strip(buckets.to), cc: strip(buckets.cc), bcc: strip(buckets.bcc) };
    // Preserve any name we already had if the incoming one is bare.
    const existing = [...buckets.to, ...buckets.cc, ...buckets.bcc]
        .find(r => r.address.toLowerCase() === addr);
    next[target] = [...next[target], { address: addr, name: recipient.name ?? existing?.name ?? null }];
    return next;
}

interface DragData {
    from: RecipientField;
    address: string;
}

interface RecipientEditorProps {
    value:     RecipientBuckets;
    onChange:  (next: RecipientBuckets) => void;
    /** Contacts to offer as type-ahead suggestions (optional). */
    contacts?: Contact[];
    autoFocus?: boolean;
    /** Width (px) of the To/Cc/Bcc label column — match the host form's labels. */
    labelWidth?: number;
    /** Gap (px) between label and field — match the host form's row gap. */
    colGap?: number;
}

/**
 * To / Cc / Bcc editor with draggable contact chips. The three fields stack
 * vertically; chips can be dragged between them to re-classify a recipient
 * (e.g. demote a To into Bcc for a referral). Free-form addresses are always
 * accepted — type an address and press Enter, comma, or Tab — and contacts
 * surface as type-ahead suggestions.
 */
export default function RecipientEditor({
    value, onChange, contacts, autoFocus, labelWidth = 56, colGap = 8,
}: RecipientEditorProps) {
    // Cc/Bcc stay hidden until revealed (or until they already hold someone, e.g.
    // a reply-all that carried over the original Cc).
    const [showCc, setShowCc] = useState(value.cc.length > 0);
    const [showBcc, setShowBcc] = useState(value.bcc.length > 0);
    const ccVisible = showCc || value.cc.length > 0;
    const bccVisible = showBcc || value.bcc.length > 0;

    function remove(field: RecipientField, address: string) {
        onChange({ ...value, [field]: value[field].filter(r => r.address !== address) });
    }

    function add(field: RecipientField, recipient: Recipient) {
        onChange(placeRecipient(value, field, recipient));
    }

    /** Add many at once in a SINGLE onChange — folding each into the accumulator
     *  rather than calling add() in a loop (which would call onChange N times off
     *  the same stale `value`, so only the last recipient would survive). This is
     *  what makes pasting a block of addresses actually add all of them. */
    function addMany(field: RecipientField, recipients: Recipient[]) {
        let next = value;
        for (const r of recipients) next = placeRecipient(next, field, r);
        onChange(next);
    }

    function handleDrop(target: RecipientField, raw: string) {
        let data: DragData;
        try { data = JSON.parse(raw); } catch { return; }
        if (!data?.address || data.from === target) return;
        const source = value[data.from].find(r => r.address === data.address);
        if (!source) return;
        if (target === "cc") setShowCc(true);
        if (target === "bcc") setShowBcc(true);
        onChange(placeRecipient(value, target, source));
    }

    return (
        <div className="recipient-editor flex flex-col gap-1.5">
            <Lane
                field="to"
                label="To"
                recipients={value.to}
                contacts={contacts}
                buckets={value}
                autoFocus={autoFocus}
                labelWidth={labelWidth}
                colGap={colGap}
                onAdd={add}
                onAddMany={addMany}
                onRemove={remove}
                onDropRecipient={handleDrop}
            />
            {!ccVisible && (
                <div
                    className="recipient-add-links flex shrink-0 items-center justify-end gap-2 text-[13px]"
                    style={{ marginLeft: labelWidth + colGap }}
                >
                    <button type="button" onClick={() => setShowCc(true)} className="recipient-add-link">
                        Add Cc
                    </button>
                </div>
            )}
            {ccVisible && (
                <Lane
                    field="cc"
                    label="Cc"
                    recipients={value.cc}
                    contacts={contacts}
                    buckets={value}
                    labelWidth={labelWidth}
                    colGap={colGap}
                    onAdd={add}
                    onAddMany={addMany}
                    onRemove={remove}
                    onDropRecipient={handleDrop}
                />
            )}
            {!bccVisible && (
                <div
                    className="recipient-add-links flex shrink-0 items-center justify-end gap-2 text-[13px]"
                    style={{ marginLeft: labelWidth + colGap }}
                >
                    <button type="button" onClick={() => setShowBcc(true)} className="recipient-add-link">
                        Add Bcc
                    </button>
                </div>
            )}
            {bccVisible && (
                <Lane
                    field="bcc"
                    label="Bcc"
                    recipients={value.bcc}
                    contacts={contacts}
                    buckets={value}
                    labelWidth={labelWidth}
                    colGap={colGap}
                    onAdd={add}
                    onAddMany={addMany}
                    onRemove={remove}
                    onDropRecipient={handleDrop}
                />
            )}
        </div>
    );
}

interface LaneProps {
    field:    RecipientField;
    label:    string;
    recipients: Recipient[];
    contacts?: Contact[];
    buckets:  RecipientBuckets;
    autoFocus?: boolean;
    labelWidth: number;
    colGap: number;
    onAdd:    (field: RecipientField, recipient: Recipient) => void;
    onAddMany: (field: RecipientField, recipients: Recipient[]) => void;
    onRemove: (field: RecipientField, address: string) => void;
    onDropRecipient: (target: RecipientField, raw: string) => void;
}

function Lane({
    field, label, recipients, contacts, buckets, autoFocus, labelWidth, colGap,
    onAdd, onAddMany, onRemove, onDropRecipient,
}: LaneProps) {
    const inputRef = useRef<HTMLInputElement>(null);
    const [query, setQuery] = useState("");
    const [dragOver, setDragOver] = useState(false);
    const [activeIdx, setActiveIdx] = useState(0);

    const usedAddresses = useMemo(() => new Set(
        [...buckets.to, ...buckets.cc, ...buckets.bcc].map(r => r.address.toLowerCase()),
    ), [buckets]);

    const suggestions = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q || !contacts?.length) return [];
        return contacts
            .filter(c => c.email && !usedAddresses.has(c.email.toLowerCase()))
            .filter(c =>
                c.email!.toLowerCase().includes(q) ||
                (c.name?.toLowerCase().includes(q) ?? false))
            .slice(0, 8);
    }, [query, contacts, usedAddresses]);

    function commitTyped() {
        const parsed = parseRecipients(query);
        if (!parsed.length) return false;
        onAddMany(field, parsed);
        setQuery("");
        setActiveIdx(0);
        return true;
    }

    // A single-line <input> collapses newlines on paste, so intercept the paste
    // and parse the raw clipboard text ourselves — splitting on commas, semicolons,
    // and newlines into separate chips. Whatever was already typed is prepended so
    // a paste after a partial token still parses cleanly.
    function onPaste(e: React.ClipboardEvent<HTMLInputElement>) {
        const pasted = e.clipboardData.getData("text");
        // Concatenate (no separator) so a partial typed token completes with the
        // first pasted fragment, mirroring how typing then committing would behave.
        const parsed = parseRecipients(query + pasted);
        if (!parsed.length) return; // nothing address-like: let the browser paste as text
        e.preventDefault();
        onAddMany(field, parsed);
        setQuery("");
        setActiveIdx(0);
    }

    function pickSuggestion(c: Contact) {
        if (!c.email) return;
        onAdd(field, { name: c.name ?? null, address: c.email.toLowerCase() });
        setQuery("");
        setActiveIdx(0);
        inputRef.current?.focus();
    }

    function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
        if (suggestions.length && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
            e.preventDefault();
            setActiveIdx(i => {
                const n = suggestions.length;
                return e.key === "ArrowDown" ? (i + 1) % n : (i - 1 + n) % n;
            });
            return;
        }
        // Enter or Tab on a highlighted suggestion picks it. (Tab is captured
        // here so it selects rather than moving focus away.)
        if (suggestions.length && (e.key === "Enter" || e.key === "Tab")) {
            e.preventDefault();
            pickSuggestion(suggestions[activeIdx] ?? suggestions[0]);
            return;
        }
        // No suggestion showing: commit whatever raw address was typed.
        if (e.key === "Enter" || e.key === "," || e.key === ";" || (e.key === "Tab" && query.trim())) {
            if (commitTyped()) e.preventDefault();
            return;
        }
        if (e.key === "Backspace" && !query && recipients.length) {
            onRemove(field, recipients[recipients.length - 1].address);
        }
    }

    return (
        <div className="recipient-editor-lane flex items-center" style={{ gap: colGap }}>
            <label
                className="recipient-editor-label shrink-0 cursor-text select-none text-[13px] text-muted"
                style={{ width: labelWidth }}
                onClick={() => inputRef.current?.focus()}
            >
                {label}
            </label>
            <div className="relative flex-1">
                <div
                    className={cn(
                        "flex min-h-[34px] cursor-text flex-wrap items-center gap-1 rounded-md border bg-surface px-1.5 transition-colors",
                        dragOver ? "border-accent border-dashed bg-accent/5" : "border-border",
                        "focus-within:border-accent",
                    )}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => {
                        e.preventDefault();
                        setDragOver(false);
                        onDropRecipient(field, e.dataTransfer.getData("application/x-recipient"));
                    }}
                    onMouseDown={(e) => {
                        // Clicking empty space focuses the input without stealing
                        // focus from a chip's drag handle / remove button.
                        if (e.target === e.currentTarget) inputRef.current?.focus();
                    }}
                >
                    {recipients.map((r) => (
                        <Chip
                            key={r.address}
                            recipient={r}
                            field={field}
                            onRemove={() => onRemove(field, r.address)}
                        />
                    ))}
                    <input
                        ref={inputRef}
                        autoFocus={autoFocus}
                        value={query}
                        onChange={(e) => { setQuery(e.target.value); setActiveIdx(0); }}
                        onKeyDown={onKeyDown}
                        onPaste={onPaste}
                        onBlur={() => { commitTyped(); }}
                        placeholder={recipients.length ? "" : "Add recipients…"}
                        // Borderless, padding-free, full-height: the input fills the
                        // field box so the whole row is one seamless click target.
                        // (Utility classes override the global `input` element rule.)
                        className="h-auto min-w-[7rem] flex-1 self-stretch rounded-none border-0 bg-transparent p-0 text-sm text-fg shadow-none outline-none placeholder:text-muted focus:outline-none"
                    />
                </div>
                {suggestions.length > 0 && (
                    <div
                        className="absolute z-[1100] mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-border bg-surface p-1 shadow-lg"
                        role="listbox"
                    >
                        {suggestions.map((c, i) => (
                            <div
                                key={c.id}
                                role="option"
                                aria-selected={i === activeIdx}
                                // onMouseDown (not onClick) so it fires before the input's onBlur.
                                onMouseDown={(e) => { e.preventDefault(); pickSuggestion(c); }}
                                onMouseEnter={() => setActiveIdx(i)}
                                className={cn(
                                    "flex w-full min-w-0 cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm",
                                    i === activeIdx ? "bg-accent text-accent-fg" : "text-fg",
                                )}
                            >
                                <span className="min-w-0 truncate">{c.name || c.email}</span>
                                {c.name && (
                                    <span className={cn("shrink-0 truncate text-xs", i === activeIdx ? "text-accent-fg/80" : "text-muted")}>
                                        · {c.email}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function Chip({
    recipient, field, onRemove, draggable = true,
}: {
    recipient: Recipient;
    field: RecipientField;
    onRemove: () => void;
    draggable?: boolean;
}) {
    const display = recipient.name || recipient.address;
    const nameAndEmail = recipient.name ? `"${recipient.name}" <${recipient.address}>` : recipient.address;
    return (
        <ContextMenu.Root>
            <ContextMenu.Trigger
                render={
                    <span
                        draggable={draggable}
                        onDragStart={draggable ? (e) => {
                            const payload: DragData = { from: field, address: recipient.address };
                            e.dataTransfer.setData("application/x-recipient", JSON.stringify(payload));
                            e.dataTransfer.effectAllowed = "move";
                        } : undefined}
                        title={recipient.name ? `${recipient.name} <${recipient.address}>` : recipient.address}
                        className={cn(
                            "inline-flex max-w-full items-center gap-0.5 rounded border border-border",
                            "bg-bg py-0.5 pl-2 pr-1 text-xs leading-none text-fg",
                            draggable && "cursor-grab active:cursor-grabbing",
                        )}
                    />
                }
            >
                <span className="min-w-0 truncate">{display}</span>
                {/* A <span>, not a <button>, to dodge the global button border/bg rule. */}
                <span
                    role="button"
                    tabIndex={0}
                    aria-label={`Remove ${recipient.address}`}
                    onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    onClick={onRemove}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onRemove(); } }}
                    className="flex h-4 w-4 shrink-0 cursor-pointer items-center justify-center rounded text-sm leading-none text-muted hover:bg-border hover:text-fg"
                >
                    ×
                </span>
            </ContextMenu.Trigger>
            <ContextMenu.Content>
                <ContextMenu.Group>
                    <ContextMenu.GroupLabel className="max-w-[260px] truncate">
                        {recipient.name ? `${recipient.name} · ${recipient.address}` : recipient.address}
                    </ContextMenu.GroupLabel>
                    <ContextMenu.Item onClick={() => void copyText(recipient.address, "email")}>
                        Copy email
                    </ContextMenu.Item>
                    {recipient.name && (
                        <ContextMenu.Item onClick={() => void copyText(nameAndEmail, "name and email")}>
                            Copy name &amp; email
                        </ContextMenu.Item>
                    )}
                </ContextMenu.Group>
                <ContextMenu.Separator />
                <ContextMenu.Group>
                    <ContextMenu.Item onClick={onRemove}>
                        Remove
                    </ContextMenu.Item>
                </ContextMenu.Group>
            </ContextMenu.Content>
        </ContextMenu.Root>
    );
}

interface RecipientChipsProps {
    value:    Recipient[];
    onChange: (next: Recipient[]) => void;
    contacts?: Contact[];
    placeholder?: string;
    autoFocus?: boolean;
}

/**
 * A single-list recipient input — chips + contact type-ahead + free-form entry,
 * without the To/Cc/Bcc structure. Used for flat guest/attendee lists (e.g. the
 * meeting scheduler). Chips aren't draggable here (nowhere to drag to) but keep
 * the right-click context menu for copying.
 */
export function RecipientChips({ value, onChange, contacts, placeholder, autoFocus }: RecipientChipsProps) {
    const inputRef = useRef<HTMLInputElement>(null);
    const [query, setQuery] = useState("");
    const [activeIdx, setActiveIdx] = useState(0);

    const used = useMemo(
        () => new Set(value.map(r => r.address.toLowerCase())),
        [value],
    );

    const suggestions = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q || !contacts?.length) return [];
        return contacts
            .filter(c => c.email && !used.has(c.email.toLowerCase()))
            .filter(c =>
                c.email!.toLowerCase().includes(q) ||
                (c.name?.toLowerCase().includes(q) ?? false))
            .slice(0, 8);
    }, [query, contacts, used]);

    function addRecipients(next: Recipient[]) {
        const merged = [...value];
        for (const r of next) {
            if (!merged.some(m => m.address.toLowerCase() === r.address.toLowerCase())) merged.push(r);
        }
        onChange(merged);
    }

    function commitTyped() {
        const parsed = parseRecipients(query);
        if (!parsed.length) return false;
        addRecipients(parsed);
        setQuery("");
        setActiveIdx(0);
        return true;
    }

    // Intercept paste so a multi-line / mixed-delimiter block fans out into chips
    // (a single-line <input> would otherwise collapse newlines before we see them).
    function onPaste(e: React.ClipboardEvent<HTMLInputElement>) {
        const pasted = e.clipboardData.getData("text");
        const parsed = parseRecipients(query + pasted);
        if (!parsed.length) return;
        e.preventDefault();
        addRecipients(parsed);
        setQuery("");
        setActiveIdx(0);
    }

    function pickSuggestion(c: Contact) {
        if (!c.email) return;
        addRecipients([{ name: c.name ?? null, address: c.email.toLowerCase() }]);
        setQuery("");
        setActiveIdx(0);
        inputRef.current?.focus();
    }

    function remove(address: string) {
        onChange(value.filter(r => r.address !== address));
    }

    function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
        if (suggestions.length && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
            e.preventDefault();
            setActiveIdx(i => {
                const n = suggestions.length;
                return e.key === "ArrowDown" ? (i + 1) % n : (i - 1 + n) % n;
            });
            return;
        }
        if (suggestions.length && (e.key === "Enter" || e.key === "Tab")) {
            e.preventDefault();
            pickSuggestion(suggestions[activeIdx] ?? suggestions[0]);
            return;
        }
        if (e.key === "Enter" || e.key === "," || e.key === ";" || (e.key === "Tab" && query.trim())) {
            if (commitTyped()) e.preventDefault();
            return;
        }
        if (e.key === "Backspace" && !query && value.length) {
            remove(value[value.length - 1].address);
        }
    }

    return (
        <div className="relative w-full">
            <div
                className="flex min-h-[34px] cursor-text flex-wrap items-center gap-1 rounded-md border border-border bg-surface px-1.5 transition-colors focus-within:border-accent"
                onMouseDown={(e) => { if (e.target === e.currentTarget) inputRef.current?.focus(); }}
            >
                {value.map((r) => (
                    <Chip
                        key={r.address}
                        recipient={r}
                        field="to"
                        draggable={false}
                        onRemove={() => remove(r.address)}
                    />
                ))}
                <input
                    ref={inputRef}
                    autoFocus={autoFocus}
                    value={query}
                    onChange={(e) => { setQuery(e.target.value); setActiveIdx(0); }}
                    onKeyDown={onKeyDown}
                    onPaste={onPaste}
                    onBlur={() => { commitTyped(); }}
                    placeholder={value.length ? "" : (placeholder ?? "Add people…")}
                    className="h-auto min-w-[7rem] flex-1 self-stretch rounded-none border-0 bg-transparent p-0 text-sm text-fg shadow-none outline-none placeholder:text-muted focus:outline-none"
                />
            </div>
            {suggestions.length > 0 && (
                <div
                    className="absolute z-[1100] mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-border bg-surface p-1 shadow-lg"
                    role="listbox"
                >
                    {suggestions.map((c, i) => (
                        <div
                            key={c.id}
                            role="option"
                            aria-selected={i === activeIdx}
                            onMouseDown={(e) => { e.preventDefault(); pickSuggestion(c); }}
                            onMouseEnter={() => setActiveIdx(i)}
                            className={cn(
                                "flex w-full min-w-0 cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm",
                                i === activeIdx ? "bg-accent text-accent-fg" : "text-fg",
                            )}
                        >
                            <span className="min-w-0 truncate">{c.name || c.email}</span>
                            {c.name && (
                                <span className={cn("shrink-0 truncate text-xs", i === activeIdx ? "text-accent-fg/80" : "text-muted")}>
                                    · {c.email}
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
